import styles from "./Footer.module.css"
import { useState } from 'react';
import {  Linkedin, Instagram } from 'lucide-react';
import smLogo from "../../assets/images/catermatLogoSmall.png"
import NewsPopup from "../Popups/News/News";
import { APP_VERSION } from "../../constants/info";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useCmsContent } from "../../hooks/useCmsContent";
import { pickCmsText } from "../../utils/cmsText";
import { CmsAktualita, CmsFooterLink } from "../../types/cms.types";
/**
* Footer.
* Contains various links and company info.
* Link labels/urls and the aktuality come from the CMS with built-in fallbacks.
* @returns TSX.Element
*/
const Footer = () => {
    const { t, i18n } = useTranslation(); // Translation
    const cmsContent = useCmsContent(); // CMS managed texts (undefined = use built-in fallbacks)
    // Handle opening news modal for a specific CMS article (undefined = built-in article)
    const [openedAktualita, setOpenedAktualita] = useState<CmsAktualita | undefined>(undefined);
    const [newsOpen, setNewsOpen] = useState<boolean>(false);

    // Resolve one footer link from the CMS with built-in fallbacks (uploaded file wins over url)
    const footerLink = (link: CmsFooterLink | undefined, fallbackLabel: string, fallbackHref: string) => ({
        label: pickCmsText(link?.label, i18n.language, fallbackLabel),
        href: link?.fileUrl || link?.url || fallbackHref,
    });

    const links = {
        about: footerLink(cmsContent?.footerLinks?.about, t("footer.about.about"), "/about"),
        policy: footerLink(cmsContent?.footerLinks?.policy, t("footer.about.policy"), "/terms"),
        allergens: footerLink(cmsContent?.footerLinks?.allergens, t("footer.about.allergens"), "/allergens"),
        contacts: footerLink(cmsContent?.footerLinks?.contacts, t("footer.about.contacts"), "/contacts"),
        gallery: footerLink(cmsContent?.footerLinks?.gallery, t("footer.about.gallery"), "https://photos.app.goo.gl/XGoTjL5S92qebNM18"),
        terms: footerLink(cmsContent?.footerLinks?.terms, t("footer.links.terms"), "/terms"),
        gdpr: footerLink(cmsContent?.footerLinks?.gdpr, t("footer.links.gdpr"), "/terms"),
        login: footerLink(cmsContent?.footerLinks?.login, t("footer.links.login"), "/login"),
    };

    // Built-in aktuality shown until the CMS provides real ones
    const fallbackNews = [
        { date: "28. 03. 2025", title: "Grilovací sezóna je tu!" },
        { date: "18. 03. 2025", title: "Energie s multicereálním croissantem" },
        { date: "06. 03. 2025", title: "Už jste ochutnali naše kachní speciality?" },
        { date: "24. 02. 2025", title: "Kachní novinky na e-shopu" },
    ];
    const cmsNews = cmsContent?.aktuality ?? [];

    const formatNewsDate = (isoDate?: string) => {
        if (!isoDate) return "";
        try {
            return format(parseISO(isoDate), "dd. MM. yyyy");
        } catch {
            return isoDate;
        }
    };

    const openAktualita = (aktualita?: CmsAktualita) => {
        setOpenedAktualita(aktualita);
        setNewsOpen(true);
    };

    return(
        <>
            <div className={styles.Footer}>
                <NewsPopup display={newsOpen} aktualita={openedAktualita} onClose={()=>setNewsOpen(false)}/>
                <div className={styles.FooterContent}>
                    <div className={styles.FooterMainPart}>
                        <div className={styles.FooterMainLogo}>
                            <img src={smLogo} alt="catermat_logo_small" />
                        </div>
                        <div className={styles.FooterMainText}>
                            <p className={styles.FooterMainTitle}>EXPRESS DELI s.r.o</p>
                            <p className={styles.FooterMainAddress}>Michelská 1552/58, Praha 4 141 00, Česká republika</p>
                        </div>
                        <div className={styles.FooterMainContact}>
                            <p>+420 222 510 446 <span>(9:00 - 17:00)</span></p>
                        </div>
                        <div className={styles.FooterMainEmail}>
                            <p>catermat@catermat.com</p>
                        </div>
                    </div>
                    <div className={styles.FooterPart}>
                        <p className={styles.FooterPartTitle}>{t("footer.about.title")}</p>
                        <div className={styles.FooterPartContent}>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.about.href}>{links.about.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.policy.href}>{links.policy.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.allergens.href}>{links.allergens.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.contacts.href}>{links.contacts.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.gallery.href} target="_blank">{links.gallery.label}</a>
                            </div>
                        </div>
                    </div>
                    <div className={styles.FooterPart}>
                        <p className={styles.FooterPartTitle}>{t("footer.links.title")}</p>
                        <div className={styles.FooterPartContent}>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.terms.href}>{links.terms.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.gdpr.href}>{links.gdpr.label}</a>
                            </div>
                            <div className={styles.FooterPartContentItem}>
                                <a href={links.login.href}>{links.login.label}</a>
                            </div>
                        </div>
                    </div>
                    <div className={styles.FooterPart}>
                        <p className={styles.FooterPartTitle}>{t("footer.news.title")}</p>
                        <div className={`${styles.FooterPartContent} ${styles.Recent}`}>
                            {cmsNews.length > 0 ? (
                                cmsNews.map((aktualita) => (
                                    <div key={aktualita._id} className={styles.FooterPartRecentItem}>
                                        <p>{formatNewsDate(aktualita.date)}</p>
                                        <a onClick={()=>openAktualita(aktualita)}>
                                            {pickCmsText(aktualita.title, i18n.language, "")}
                                        </a>
                                    </div>
                                ))
                            ) : (
                                fallbackNews.map((item) => (
                                    <div key={item.title} className={styles.FooterPartRecentItem}>
                                        <p>{item.date}</p>
                                        <a onClick={()=>openAktualita(undefined)}>{item.title}</a>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.FooterLegal}>
                    <div className={styles.FooterLegalCopyright}>
                        <p>© Copyright EXPRESS DELI s.r.o.</p>
                    </div>
                    <div className={styles.FooterLegalContact}>
                        <p>+420 222 510 446 | catermat@catermat.com</p>
                    </div>
                    <div className={styles.FooterLegalSocials}>
                        <Link to={"https://cz.linkedin.com/company/catermat-com"} target="_blank">
                            <Linkedin />
                        </Link>
                        <Link to={"https://www.instagram.com/catermatcom/"} target="_blank">
                            <Instagram />
                        </Link>
                    </div>
                </div>
                <p className={styles.Version}>{APP_VERSION}</p>
            </div>
        </>
    )
}

export default Footer;
